// Standalone helpers + leaf components for the Crew & Network page.
// Extracted from Network.jsx (P3.c). These have no dependency on the page's
// local state, so they live here to keep Network.jsx focused on the main
// component.

import { useState } from "react";
import { Copy, Check, History, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../../lib/api";
import { Input } from "../../../components/ui/input";
import { AuditTimeline } from "../../../components/AuditTimeline";

export const RANK = {
  owner: 100, manager: 75, captain: 50, first_officer: 40,
  engineer: 20, accountant: 20,
  deckhand: 10, stewardess: 10, chef: 10, hostess: 10, cleaner: 10,
  detailer: 10, delivery_crew: 10, night_watch: 10, maintenance_helper: 10,
  provider: 10,
};

export const ROLE_LABELS = {
  owner: "Owner", manager: "Manager", captain: "Captain", first_officer: "First Officer",
  engineer: "Engineer", accountant: "Accountant",
  deckhand: "Deckhand", stewardess: "Stewardess", chef: "Chef", hostess: "Hostess",
  cleaner: "Cleaner", detailer: "Detailer", delivery_crew: "Delivery Crew",
  night_watch: "Night Watch", maintenance_helper: "Maintenance Helper",
  provider: "Online Supplier",
};

// Operational roles a captain-or-above may always invite, in display order.
const OPS_ROLES = [
  "first_officer", "engineer", "accountant", "deckhand", "stewardess",
  "chef", "hostess", "cleaner", "detailer", "delivery_crew",
  "night_watch", "maintenance_helper", "provider",
];

// Build the role allow-list for an invite, exactly matching the backend.
export function rolesAllowedFor(callerRole, existingRoles) {
  if (callerRole === "owner") {
    return ["owner", "manager", "captain", ...OPS_ROLES];
  }
  if (callerRole === "manager") {
    const list = ["captain", ...OPS_ROLES];
    if (!existingRoles.has("owner")) list.unshift("owner");
    return list;
  }
  if (callerRole === "captain") {
    const list = [...OPS_ROLES];
    if (!existingRoles.has("manager")) list.unshift("manager");
    if (!existingRoles.has("owner")) list.unshift("owner");
    return list;
  }
  if (callerRole === "first_officer") {
    // "Almost captain": can invite any role below captain, but not a senior
    // (owner / manager / captain / another first_officer).
    return OPS_ROLES.filter((r) => r !== "first_officer");
  }
  return [];
}

export const emptyForm = () => ({
  type: "company",
  name: "",
  contact_person: "",
  email: "",
  phone: "",
  categories: [],
  category: "",
  notes: "",
  // Business details — field names match the marketplace "Company details"
  // exactly so offline and online suppliers are tracked identically.
  website: "",
  vat_number: "",
  tax_id: "",
  bank_name: "",
  bank_account_holder: "",
  bank_account_number: "",
  bank_iban: "",
  bank_bic_swift: "",
  address: "",
  city: "",
  postcode: "",
  country: "",
});

/**
 * Compact labeled input used inside the supplier-edit "Business details"
 * section. Kept bespoke (smaller label text, tighter spacing).
 */
export function SupBizField({ label, value, onChange, testid, placeholder, disabled }) {
  return (
    <div>
      <label className="text-xs font-medium text-zinc-600">{label}</label>
      <Input
        className="mt-1"
        value={value || ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        disabled={disabled}
        readOnly={disabled}
      />
    </div>
  );
}

// Collapsible audit-history panel for the supplier Info modal. Lazy-loads
// the supplier's audit_log rows (create / update / assign / invite / unassign
// / delete) on first expand and renders them via the shared <AuditTimeline>.
export function SupplierHistory({ supplierId }) {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && rows === null) {
      try {
        const { data } = await api.get(`/audit?ref_kind=supplier&ref_id=${supplierId}`);
        setRows(Array.isArray(data) ? data : []);
      } catch {
        setRows([]);
      }
    }
  };

  return (
    <div className="pt-1 border-t border-zinc-100" data-testid="supplier-history">
      <button
        type="button"
        onClick={toggle}
        className="flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-zinc-500 hover:text-zinc-800 transition-colors mt-2"
        data-testid="supplier-history-toggle"
      >
        <History className="h-3.5 w-3.5" /> History
        {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
      </button>
      {open && (
        <div className="mt-2 max-h-56 overflow-y-auto pr-1">
          <AuditTimeline rows={rows} emptyText="No history yet." testidPrefix="supplier-history" />
        </div>
      )}
    </div>
  );
}

export function BizRow({ k, v, mono, copyable }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(String(v));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      toast.success(`${k} copied`);
    } catch {
      toast.error("Couldn't copy to clipboard");
    }
  };
  return (
    <div className="grid grid-cols-3 gap-2 items-center">
      <div className="text-xs uppercase tracking-wider text-zinc-500">{k}</div>
      <div className={"col-span-2 flex items-center gap-2 min-w-0"}>
        <span className={"truncate flex-1 " + (mono ? "font-mono text-[13px]" : "")}>{v}</span>
        {copyable && (
          <button
            type="button"
            onClick={onCopy}
            className="shrink-0 p-1 rounded text-zinc-400 hover:text-ink hover:bg-zinc-100"
            title={`Copy ${k}`}
            data-testid={`copy-${k.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          >
            {copied
              ? <Check className="h-3.5 w-3.5 text-emerald-600" />
              : <Copy className="h-3.5 w-3.5" />}
          </button>
        )}
      </div>
    </div>
  );
}

export function BizAddress({ addr }) {
  const [copied, setCopied] = useState(false);
  // Standard postal block: each line on its own row, no commas.
  const lines = [
    addr.address || [addr.address_line1, addr.address_line2].filter(Boolean).join(", "),
    [addr.postcode, addr.city].filter(Boolean).join(" "),
    addr.country,
  ].filter(Boolean);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      toast.success("Address copied");
    } catch {
      toast.error("Couldn't copy to clipboard");
    }
  };
  return (
    <div className="grid grid-cols-3 gap-2 items-start" data-testid="biz-address">
      <div className="text-xs uppercase tracking-wider text-zinc-500 mt-0.5">Address</div>
      <div className="col-span-2 flex items-start gap-2 min-w-0">
        <div className="flex-1 min-w-0">
          {lines.map((line, i) => (
            <div key={i} className="truncate">{line}</div>
          ))}
        </div>
        <button
          type="button"
          onClick={onCopy}
          className="shrink-0 p-1 rounded text-zinc-400 hover:text-ink hover:bg-zinc-100"
          title="Copy address"
          data-testid="copy-address"
        >
          {copied
            ? <Check className="h-3.5 w-3.5 text-emerald-600" />
            : <Copy className="h-3.5 w-3.5" />}
        </button>
      </div>
    </div>
  );
}
